import { TRPCClientError, type TRPCLink } from '@trpc/client';
import { observable, type Observable, type Observer } from '@trpc/server/observable';
import type { AppRouter } from '@servwell/api/router';
import { browserLocks, type ExclusiveLocks } from './browser';
import { idOf, keyOf } from './idempotency';
import { isReplayable, type PendingWrites } from './pending-writes';

/**
 * The tRPC link in front of `httpBatchLink`: the one guard a mutation in retry back-off
 * cannot get round. TanStack's retryer sends again after its sleep even when the mutation
 * has been removed from the cache, and a Mutation has no public cancel; only this sees every
 * send. It reads the raw `op.path` and `op.input` before superjson does, and asks
 * `PendingWrites.holdFor` whether this tab may send it now.
 *
 * **Two tabs of one account.** A replayable write is sent under a QUEUED per-row Web Lock,
 * held until the downstream request SETTLES (its terminal next/error/complete), never
 * released on unsubscribe: `httpBatchLink`'s teardown is a no-op and the request runs on. A
 * second tab that restored the same row waits, checks again, and sends; the server returns
 * the logged answer, which holds because every replayable write is database-only
 * (`apps/api/test/replayable-writes-database-only.test.ts`). It never skips silently. If the
 * holder's tab dies, the browser releases its lock and the waiter goes.
 *
 * Residuals, stated: a browser without Web Locks has no lock (the server's key still dedupes
 * database-only writes); a tab closed WHILE its request is in flight releases its lock while
 * the server may still be running it, and there the server's own key lock (MAP 233) makes the
 * second copy wait: it then replays the first's answer, or runs, if the first rolled back.
 */

export function writeLockName(rowId: string): string {
  return `servwell-write:${rowId}`;
}

export function writeGuardLink(
  writes: Pick<PendingWrites, 'holdFor'>,
  opts: { locks?: ExclusiveLocks | null } = {},
): TRPCLink<AppRouter> {
  const locks = opts.locks === undefined ? browserLocks() : (opts.locks ?? undefined);
  return () =>
    ({ op, next }) => {
      if (op.type !== 'mutation') return next(op);
      return observable((observer) => {
        const refuse = (err: Error) => observer.error(TRPCClientError.from(err));
        const held = writes.holdFor(op.path, op.input);
        if (held) return refuse(held);
        const key = isReplayable(op.path) ? keyOf(op.input) : null;
        if (!key || !locks) return next(op).subscribe(observer);
        let granted = false;
        locks
          .request(writeLockName(idOf(key)), {}, () => {
            granted = true;
            // Time passed while waiting: whoever this tab writes as now decides.
            const again = writes.holdFor(op.path, op.input);
            if (again) {
              refuse(again);
              return Promise.resolve();
            }
            return sendHoldingLock(next(op), observer);
          })
          .catch((err: unknown) => {
            // The lock itself failed (no lock manager in this context): send without one,
            // the stated residual, rather than strand the write.
            if (!granted) next(op).subscribe(observer);
            else refuse(err as Error);
          });
        // Deliberately nothing to tear down: the lock is held until the request settles.
        return () => {};
      });
    };
}

/**
 * Send downstream and resolve (releasing the lock) on the request's first terminal event,
 * whatever happens upstream meanwhile.
 */
function sendHoldingLock<T, E>(
  request: Observable<T, E>,
  observer: Observer<T, E>,
): Promise<void> {
  return new Promise<void>((release) => {
    request.subscribe({
      next(value) {
        release();
        observer.next(value);
      },
      error(err) {
        release();
        observer.error(err);
      },
      complete() {
        release();
        observer.complete();
      },
    });
  });
}
